import {
  OcularMotilityData,
  SmoothPursuitDirectionMetrics,
  SmoothPursuitMetrics,
  SmoothPursuitSnapshot,
  SmoothPursuitTestConfig,
  SmoothPursuitVelocityUnavailableReason,
} from "types/testTypes";

export type SmoothPursuitDirection = "clockwise" | "counterClockwise";
export type SmoothPursuitEye = "left" | "right";

/** Did the eye keep up with the target? The headline verdict. */
export type SmoothPursuitTrackingLabel = "normal" | "borderline" | "review";
/** How it kept up: by pursuing smoothly, or by stepping after the target. */
export type SmoothPursuitQualityLabel = "smooth" | "elevated" | "saccadic";
export type SmoothPursuitRiskLabel = SmoothPursuitTrackingLabel | "insufficient";

export const SMOOTH_PURSUIT_DIRECTIONS: SmoothPursuitDirection[] = [
  "clockwise",
  "counterClockwise",
];

/**
 * Why the headline number is `inclusiveGain`, and not `amplitudeRatio` or
 * `pursuitGain`.
 *
 * All three measure something real, and the verdict needs the one that answers
 * "did the eye keep up with the target":
 *
 * - `pursuitGain` is *desaccaded* - the median velocity ratio measured only
 *   between catch-up saccades - so a healthy eye reads ~0.80 on it by
 *   construction. Banding that against the ~1.0 the literature quotes compared
 *   two different quantities and flagged roughly half of every recording we
 *   hold. It stays on the card as mechanism detail.
 * - `amplitudeRatio` is the *size* of the eye's orbit over the target's. It is
 *   well behaved, but it is blind to whether the orbit was traced in step: gaze
 *   scattered over a region of the right extent scores well on it. One recording
 *   in this audit read 1.32 on it with a 22 deg median error and a gain of zero.
 * - `inclusiveGain` is the same signed projection as `pursuitGain` taken over
 *   every sample and as a mean, so it counts the catch-up saccades that carry
 *   ~27% of a tracking eye's travel. It is on the scale published gains use and
 *   it is sensitive to exactly what the verdict is about. On the recording above
 *   it reads -0.65: the eye's mean motion was *against* the target.
 *
 * The cut points are the p5 and p25 of its own distribution, which is the same
 * anchor the `amplitudeRatio` bands used (their 0.75 / 0.85 sat at that
 * distribution's p5 0.766 / p25 0.877). Measured over 451 direction x eye cells
 * from 145 recordings across both databases, selected on data quality alone
 * (valid-sample fraction >= 0.95) plus the usability gate below, so the limits
 * are not circular: p5 0.508 / p10 0.671 / p25 0.815 / p50 0.886 / p95 1.058.
 *
 * Do not reuse the `amplitudeRatio` cut points here. This distribution has a far
 * fatter left tail (p5 0.508 against 0.766), so 0.85 / 0.75 on it flags more
 * than the bands it replaces: 48% of recordings normal against this file's 57%,
 * and 36% if the worst cell decides - the flag storm in a different costume.
 *
 * STILL PROVISIONAL: this is the distribution of Oculera recordings of unknown
 * clinical status, not a validated normative cohort. The quantity and the scale
 * are right; the limits are not clinical ones and must be replaced once a real
 * cohort exists. Do not tune by eye.
 */
export const SMOOTH_PURSUIT_GAIN_BANDS = {
  expectedLow: 0.8,
  reviewLow: 0.5,
} as const;

/**
 * An eye cannot sweep a wider angular orbit than the target it is tracking, so
 * an `amplitudeRatio` above this is physically implausible while tracking and
 * usually means a per-eye calibration fault in the headset.
 *
 * It is reported as a note beside the quality badge and deliberately does NOT
 * carry the verdict. It used to: a `calibrationSuspect` risk state was checked
 * ahead of the bands, and it fired on 21 of the 145 recordings we hold - one in
 * seven - because `orbitRadius` is the median distance of the samples from their
 * own median position, which measures how far the gaze *wandered* and needs no
 * orbit to have been traced at all. A trial the patient never performed cleared
 * 1.05 easily and was reported as a device fault, telling the operator to
 * recalibrate a headset that was working.
 *
 * Keeping it visible but off the verdict is a deliberate interim, and it is a
 * real trade: of those 21, the usability gate catches 5, but 12 now read
 * `normal` and 10 of those have a ratio above 1.2. The note is what stops them
 * reading clean. A verdict-carrying check has to test whether the two eyes
 * disagree with *each other* - the eyes in these recordings agree, so a
 * conjugate offset is not a per-eye calibration fault - and that needs the
 * headset to record its calibration state. See SD-1689.
 *
 * The note itself shows on 16 of the 145.
 */
export const SMOOTH_PURSUIT_IMPLAUSIBLE_AMPLITUDE_ABOVE = 1.05;

/**
 * The mechanism axis: what fraction of samples were excised as saccadic.
 *
 * Deliberately not a standalone verdict. Its reference spread is wide enough
 * (p5 0.073 to p95 0.318) that it tracks data quality and tracking behaviour
 * far more than pathology, and any single threshold on it reproduces the flag
 * storm this change exists to remove. But keeping up with the target by stepping
 * after it is the clinically interesting pattern and has to stay visible, so it
 * escalates `normal` to `borderline` and never further.
 *
 * That escalation is now load bearing. Against the `amplitudeRatio` bands it
 * fired on none of the recordings we hold, because a cell masked this heavily
 * always had coverage below the expected floor already. Against the gain bands
 * it fires on 3 of 145 - `inclusiveGain` counts the catch-up saccades, so a
 * recording can reach a normal gain *by* stepping and this is the only thing
 * that says so.
 */
export const SMOOTH_PURSUIT_QUALITY_BANDS = {
  elevatedAbove: 0.25,
  saccadicAbove: 0.35,
} as const;

/**
 * Above this median tracking error a cell has not measured pursuit at all, so it
 * is excluded from the verdict rather than banded into one.
 *
 * On a 10 deg orbit, gaze sitting more than 12 deg from the target is more than
 * one orbit diameter away - whatever gain or coverage that cell reports is a
 * number about a trial that did not happen.
 *
 * It moves 13 of the 145 recordings we hold out of a band and into
 * `insufficient`: 8 that read `review`, a clinical finding, and 5 that read
 * `calibrationSuspect`, a device fault. Neither claim is one the data supports,
 * and "repeat the test" is the honest one. It is not trimming edges - all 5 of
 * the former calibration cases fail it on every one of their four cells - but it
 * is also not the main reason a cell is excluded: across the 21 recordings that
 * end up `insufficient`, 42 cells carry an `unavailableReason`, 26 fail this
 * gate, and 14 are an empty direction's zeros.
 *
 * This replaces a two-tier error guard that pulled cells to `borderline` above
 * 4 deg and `review` above 8 deg. That guard existed because `amplitudeRatio` is
 * blind to whether the orbit was traced in step; `inclusiveGain` measures that
 * directly, so keeping both would count median error towards the verdict twice.
 * The tighter thresholds were also close to the noise: roughly 20-30% of the
 * median error we report is static per-eye calibration bias rather than tracking
 * error, which 12 deg has ample headroom over and 4 deg did not.
 */
export const SMOOTH_PURSUIT_ERROR_GATE_DEG = 12;

/**
 * A verdict needs at least this many of the four direction x eye cells. Below
 * it the result reads `insufficient` rather than borrowing a label from one
 * surviving cell.
 */
export const MIN_USABLE_SMOOTH_PURSUIT_CELLS = 2;

/**
 * And this many usable cells have to fall below a band before it is applied.
 *
 * Taking the worst of four cells makes the verdict a function of the left tail:
 * four cells that each have a ~10% chance of landing below the expected floor
 * flag about a third of recordings on tail alone. Worst-of-four puts 50% of the
 * recordings we hold at `normal` against this rule's 57%, and the whole
 * difference is `borderline`: 36 recordings against 26, with `review` identical
 * at 15 either way. It is single-cell tails, not deficits.
 *
 * Requiring corroboration costs no clinically meaningful pattern, because none
 * of them are confined to one cell: a unilateral deficit shows up in both
 * directions of one eye, and a unidirectional one in both eyes of one direction
 * - two cells either way. A single failing cell is far more often a blink, a
 * lapse of attention, or one bad half.
 *
 * Deliberately an absolute count rather than a fraction of the usable cells.
 * 115 of the 145 recordings we hold have all four usable, and over the whole set
 * the two rules disagree on none of them, so the simpler one is the honest one. The corner it creates is a
 * recording down to exactly two usable cells, where one bad cell can no longer
 * reach a band - which is the rule working as intended: with two cells left,
 * one of them is still uncorroborated.
 */
export const MIN_FAILING_SMOOTH_PURSUIT_CELLS = 2;

const MIN_EYE_OPENNESS = 0.5;
const MAX_GAZE_ECCENTRICITY_DEG = 45;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const finiteOrNull = (value: unknown): number | null => (isFiniteNumber(value) ? value : null);

const numberOrZero = (value: unknown): number => finiteOrNull(value) ?? 0;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const roundUpToStep = (value: number, step: number) => Math.ceil(value / step) * step;

export function buildSmoothPursuitStepTicks(maxValue: number, step = 2): number[] {
  const safeMax = isFiniteNumber(maxValue) ? Math.max(0, maxValue) : 0;
  const upper = Math.max(step, roundUpToStep(safeMax, step));
  const ticks: number[] = [];

  for (let tick = 0; tick <= upper; tick += step) {
    ticks.push(tick);
  }

  return ticks;
}

export function normalizeSmoothPursuitChartData(data: OcularMotilityData[]): OcularMotilityData[] {
  if (!data.length) return [];

  const t0 = data.find((point) => isFiniteNumber(point.timestamp))?.timestamp ?? 0;

  return data.map((point) => ({
    ...point,
    timestamp: isFiniteNumber(point.timestamp) ? Number((point.timestamp - t0).toFixed(3)) : 0,
  }));
}

export function splitSmoothPursuitChartData(
  data: OcularMotilityData[],
  config: SmoothPursuitTestConfig | null | undefined,
): Record<SmoothPursuitDirection, OcularMotilityData[]> {
  const clockwiseDuration = finiteOrNull(config?.clockwiseDuration);

  if (clockwiseDuration === null) {
    return { clockwise: data, counterClockwise: [] };
  }

  return {
    clockwise: data.filter((point) => point.timestamp < clockwiseDuration),
    counterClockwise: data.filter((point) => point.timestamp >= clockwiseDuration),
  };
}

export function getSmoothPursuitErrorAxis(data: OcularMotilityData[], step = 2) {
  let maxTimestamp = 0;
  let maxError = 0;

  for (const point of data) {
    if (isFiniteNumber(point.timestamp) && point.timestamp > maxTimestamp) {
      maxTimestamp = point.timestamp;
    }
    if (isFiniteNumber(point.leftEyeError) && point.leftEyeError > maxError) {
      maxError = point.leftEyeError;
    }
    if (isFiniteNumber(point.rightEyeError) && point.rightEyeError > maxError) {
      maxError = point.rightEyeError;
    }
  }

  const xMax = buildSmoothPursuitStepTicks(maxTimestamp, step).at(-1) ?? step;
  const yMax = buildSmoothPursuitStepTicks(maxError + step, step).at(-1) ?? step;

  return {
    xMax,
    xTicks: buildSmoothPursuitStepTicks(maxTimestamp, step),
    yMax,
    yTicks: buildSmoothPursuitStepTicks(maxError + step, step),
  };
}

export function getSmoothPursuitDirectionMetrics(
  metrics: SmoothPursuitMetrics | null | undefined,
  direction: SmoothPursuitDirection,
): SmoothPursuitDirectionMetrics | null {
  if (!metrics) return null;

  const directionMetrics = metrics[direction];
  if (directionMetrics) return directionMetrics;

  return {
    leftMedianError: numberOrZero(metrics.leftMedianError ?? metrics.leftMeanError),
    rightMedianError: numberOrZero(metrics.rightMedianError ?? metrics.rightMeanError),
    leftPursuitGain: numberOrZero(metrics.leftPursuitGain),
    rightPursuitGain: numberOrZero(metrics.rightPursuitGain),
    leftMeanTangentialVelocity: numberOrZero(metrics.leftMeanTangentialVelocity),
    rightMeanTangentialVelocity: numberOrZero(metrics.rightMeanTangentialVelocity),
    leftSaccadeCount: numberOrZero(metrics.leftSaccadeCount),
    rightSaccadeCount: numberOrZero(metrics.rightSaccadeCount),
    leftQualityDroppedFrames: numberOrZero(metrics.leftQualityDroppedFrames),
    rightQualityDroppedFrames: numberOrZero(metrics.rightQualityDroppedFrames),
    leftOnsetDroppedFrames: numberOrZero(metrics.leftOnsetDroppedFrames),
    rightOnsetDroppedFrames: numberOrZero(metrics.rightOnsetDroppedFrames),
    // Absent has to stay absent: a manufactured 0 reads as a total tracking
    // failure on the gain band and as perfect smoothness on the quality one.
    leftInclusiveGain: finiteOrNull(metrics.leftInclusiveGain) ?? undefined,
    rightInclusiveGain: finiteOrNull(metrics.rightInclusiveGain) ?? undefined,
    leftAmplitudeRatio: finiteOrNull(metrics.leftAmplitudeRatio) ?? undefined,
    rightAmplitudeRatio: finiteOrNull(metrics.rightAmplitudeRatio) ?? undefined,
    leftSaccadeMaskedFraction: finiteOrNull(metrics.leftSaccadeMaskedFraction) ?? undefined,
    rightSaccadeMaskedFraction: finiteOrNull(metrics.rightSaccadeMaskedFraction) ?? undefined,
    leftVelocityUnavailableReason: metrics.leftVelocityUnavailableReason,
    rightVelocityUnavailableReason: metrics.rightVelocityUnavailableReason,
  };
}

/** One direction x eye cell, with absence preserved as `null` throughout. */
export interface SmoothPursuitCell {
  direction: SmoothPursuitDirection;
  eye: SmoothPursuitEye;
  /** The saccade-inclusive velocity gain: the headline - see the bands. */
  inclusiveGain: number | null;
  /** Orbit size. Mechanism detail, plus the implausibility note above 1.05. */
  amplitudeRatio: number | null;
  medianError: number | null;
  /** The desaccaded velocity gain: mechanism detail, never the headline. */
  pursuitGain: number | null;
  saccadeMaskedFraction: number | null;
  unavailableReason: SmoothPursuitVelocityUnavailableReason | null;
}

function readCell(
  metrics: SmoothPursuitMetrics | null | undefined,
  direction: SmoothPursuitDirection,
  eye: SmoothPursuitEye,
): SmoothPursuitCell {
  const source: Partial<SmoothPursuitDirectionMetrics> = metrics?.[direction] ?? metrics ?? {};

  return {
    direction,
    eye,
    inclusiveGain: finiteOrNull(
      eye === "left" ? source.leftInclusiveGain : source.rightInclusiveGain,
    ),
    amplitudeRatio: finiteOrNull(
      eye === "left" ? source.leftAmplitudeRatio : source.rightAmplitudeRatio,
    ),
    medianError: finiteOrNull(eye === "left" ? source.leftMedianError : source.rightMedianError),
    pursuitGain: finiteOrNull(eye === "left" ? source.leftPursuitGain : source.rightPursuitGain),
    saccadeMaskedFraction: finiteOrNull(
      eye === "left" ? source.leftSaccadeMaskedFraction : source.rightSaccadeMaskedFraction,
    ),
    unavailableReason:
      (eye === "left"
        ? source.leftVelocityUnavailableReason
        : source.rightVelocityUnavailableReason) ?? null,
  };
}

export function getSmoothPursuitCells(
  metrics: SmoothPursuitMetrics | null | undefined,
): SmoothPursuitCell[] {
  return SMOOTH_PURSUIT_DIRECTIONS.flatMap((direction) =>
    (["left", "right"] as const).map((eye) => readCell(metrics, direction, eye)),
  );
}

function isUsableEye(
  angles: { x: number; y: number } | null | undefined,
  valid: boolean | undefined,
  openness: number | undefined,
) {
  if (angles == null || valid !== true) return false;
  if (isFiniteNumber(openness) && openness < MIN_EYE_OPENNESS) return false;
  return Math.hypot(angles.x, angles.y) <= MAX_GAZE_ECCENTRICITY_DEG;
}

function getQualityDroppedFromSnapshots(snapshots: SmoothPursuitSnapshot[], eye: SmoothPursuitEye) {
  let dropped = 0;

  for (const snapshot of snapshots) {
    const usable =
      eye === "left"
        ? isUsableEye(snapshot.leftGazeAngles, snapshot.leftGazeValid, snapshot.leftEyeOpenness)
        : isUsableEye(snapshot.rightGazeAngles, snapshot.rightGazeValid, snapshot.rightEyeOpenness);

    if (!usable) dropped += 1;
  }

  return dropped;
}

export function getValidSamplePercent(totalSamples: number, droppedFrames: number): number | null {
  if (!Number.isFinite(totalSamples) || totalSamples <= 0) return null;

  const dropped = clamp(Math.max(0, droppedFrames), 0, totalSamples);
  return ((totalSamples - dropped) / totalSamples) * 100;
}

export function getSmoothPursuitValidity(
  snapshots: SmoothPursuitSnapshot[],
  metrics: SmoothPursuitMetrics | null | undefined,
) {
  const totalSamples = snapshots.length;

  const leftDropped =
    finiteOrNull(metrics?.leftQualityDroppedFrames) ??
    getQualityDroppedFromSnapshots(snapshots, "left");
  const rightDropped =
    finiteOrNull(metrics?.rightQualityDroppedFrames) ??
    getQualityDroppedFromSnapshots(snapshots, "right");

  return {
    left: getValidSamplePercent(totalSamples, leftDropped),
    right: getValidSamplePercent(totalSamples, rightDropped),
  };
}

/**
 * A cell counts towards the verdict only if it actually measured pursuit.
 *
 * Four ways it can fail to, and none of them may be read as a gain of zero:
 *
 * - The backend reports a gain of 0 alongside an `unavailableReason` when
 *   velocity could not be estimated at all. Reading that 0 as a real value is
 *   what used to turn a short recording into a clinical "review".
 * - A backend predating SD-1689 sends no `inclusiveGain` at all.
 * - A direction with no snapshots comes back as a full set of zeros with no
 *   reason at all (`emptyDirectionMetrics`). A zero orbit radius is the tell:
 *   an eye that produced any samples has a positive one.
 * - Past the error gate the gaze was more than an orbit diameter from the
 *   target, so whatever the gain says, it is not about tracking.
 */
const isUsableCell = (cell: SmoothPursuitCell) =>
  cell.unavailableReason === null &&
  cell.inclusiveGain !== null &&
  cell.amplitudeRatio !== null &&
  cell.amplitudeRatio > 0 &&
  cell.medianError !== null &&
  cell.medianError <= SMOOTH_PURSUIT_ERROR_GATE_DEG;

/** The four headline numbers the result card shows: the banded gain per cell. */
export function getSmoothPursuitGainValues(metrics: SmoothPursuitMetrics | null | undefined) {
  return getSmoothPursuitCells(metrics).map((cell) => ({
    direction: cell.direction,
    eye: cell.eye,
    value: isUsableCell(cell) ? cell.inclusiveGain : null,
  }));
}

/**
 * Band the usable cells on gain, requiring corroboration across cells rather
 * than taking the worst one - see `MIN_FAILING_SMOOTH_PURSUIT_CELLS`.
 *
 * A cell below `reviewLow` is necessarily below `expectedLow` too, so testing
 * review first is what makes it take precedence.
 */
function trackingForCells(cells: SmoothPursuitCell[]): SmoothPursuitTrackingLabel {
  const gains = cells.map((cell) => cell.inclusiveGain ?? 0);
  const countBelow = (limit: number) => gains.filter((gain) => gain < limit).length;

  if (countBelow(SMOOTH_PURSUIT_GAIN_BANDS.reviewLow) >= MIN_FAILING_SMOOTH_PURSUIT_CELLS) {
    return "review";
  }
  if (countBelow(SMOOTH_PURSUIT_GAIN_BANDS.expectedLow) >= MIN_FAILING_SMOOTH_PURSUIT_CELLS) {
    return "borderline";
  }

  return "normal";
}

function qualityForCells(cells: SmoothPursuitCell[]): SmoothPursuitQualityLabel | null {
  const fractions = cells
    .map((cell) => cell.saccadeMaskedFraction)
    .filter((value): value is number => value !== null);

  if (!fractions.length) return null;

  if (fractions.some((value) => value > SMOOTH_PURSUIT_QUALITY_BANDS.saccadicAbove)) {
    return "saccadic";
  }
  if (fractions.some((value) => value > SMOOTH_PURSUIT_QUALITY_BANDS.elevatedAbove)) {
    return "elevated";
  }
  return "smooth";
}

export interface SmoothPursuitAssessment {
  /** What the result card shows and colours by. */
  risk: SmoothPursuitRiskLabel;
  /** Null when the recording was `insufficient` and no band was applied. */
  tracking: SmoothPursuitTrackingLabel | null;
  quality: SmoothPursuitQualityLabel | null;
  usableCells: number;
  totalCells: number;
  /** True when saccadic substitution pulled an otherwise normal result down. */
  escalatedByQuality: boolean;
  /**
   * True when a usable cell reports an orbit wider than the target's. Shown as a
   * note beside the quality badge; it does not affect `risk`. See
   * `SMOOTH_PURSUIT_IMPLAUSIBLE_AMPLITUDE_ABOVE`.
   */
  amplitudeImplausible: boolean;
}

/**
 * Usability first, then two axes.
 *
 * `insufficient` takes precedence over everything: a cell that did not measure
 * pursuit is excluded rather than banded (`isUsableCell`), and with fewer than
 * two left the recording has no verdict to give. That is what absorbed the old
 * `calibrationSuspect` state - see `SMOOTH_PURSUIT_ERROR_GATE_DEG`.
 *
 * Tracking (`inclusiveGain`) then carries the verdict. Pursuit quality
 * (`saccadeMaskedFraction`) describes the mechanism and may escalate `normal` to
 * `borderline`, but never produces a `review` of its own - see
 * `SMOOTH_PURSUIT_QUALITY_BANDS` for why.
 */
export function assessSmoothPursuit(
  metrics: SmoothPursuitMetrics | null | undefined,
): SmoothPursuitAssessment | null {
  if (!metrics) return null;

  const cells = getSmoothPursuitCells(metrics);
  const usable = cells.filter(isUsableCell);
  const base = {
    totalCells: cells.length,
    usableCells: usable.length,
    escalatedByQuality: false,
  };

  if (usable.length < MIN_USABLE_SMOOTH_PURSUIT_CELLS) {
    return {
      ...base,
      risk: "insufficient",
      tracking: null,
      quality: null,
      amplitudeImplausible: false,
    };
  }

  const quality = qualityForCells(usable);
  const amplitudeImplausible = usable.some(
    (cell) => (cell.amplitudeRatio ?? 0) > SMOOTH_PURSUIT_IMPLAUSIBLE_AMPLITUDE_ABOVE,
  );

  const tracking = trackingForCells(usable);
  const escalatedByQuality = tracking === "normal" && quality === "saccadic";

  return {
    ...base,
    risk: escalatedByQuality ? "borderline" : tracking,
    tracking,
    quality,
    escalatedByQuality,
    amplitudeImplausible,
  };
}
